import { useMemo } from 'react';
import {
  AuthStateEnum,
  SearchProfile,
  SearchProfileFactory,
  type SubscriptionPreview,
} from 'app/domain';
import { useGallery } from 'app/hooks';

const factory = new SearchProfileFactory();

type LegacyGallery = {
  // Wire-level field, set on the response's top level (search_controller_base.rb's
  // hasCustomMediaAgreements: current_user.has_custom_media_agreements?) - not
  // nested under searchProfile.
  hasCustomMediaAgreements?: boolean;
  searchProfile?: {
    allowsNudity?: boolean;
    excludeNudity?: boolean;
    isAuthenticated?: boolean;
    isAnonymous?: boolean;
    isSubscriptionRepackagingActive: boolean;
    upsellTestEligible: boolean;
    hasNLSKeywordInterleaveForMostPopular: boolean;
    ezaCompUser: boolean;
    hasEssentialSubscription: boolean;
    hasGeoPricePromo: boolean;
    isMobile?: boolean;
    isPaPreview?: boolean;
    botOrFirstTimeVisitor?: boolean;
    subscriptionPreview?: SubscriptionPreview;
    locale?: string;
    hasRfSubscription?: boolean;
  };
};

// Rails serializes `isAuthenticated` and `isAnonymous` as two independent
// booleans, not complements — the middle "known" case is `isAuthenticated:
// false, isAnonymous: false` (remembered from a prior session, not
// authenticated this one). Both are optional here for the same reason
// allowsNudity/excludeNudity are: they can be absent on an older cached
// response. Undefined for both falls through to ANONYMOUS, the
// least-privileged reading when the wire says nothing.
const toAuthState = (
  isAuthenticated?: boolean,
  isAnonymous?: boolean
): AuthStateEnum => {
  if (isAuthenticated) return AuthStateEnum.AUTHENTICATED;
  if (isAnonymous === false) return AuthStateEnum.KNOWN;

  return AuthStateEnum.ANONYMOUS;
};

const useSearchProfileAdapter = (): SearchProfile => {
  const gallery = useGallery() as unknown as LegacyGallery;
  const { hasCustomMediaAgreements = false } = gallery ?? {};
  const {
    isSubscriptionRepackagingActive = false,
    upsellTestEligible = false,
    hasNLSKeywordInterleaveForMostPopular = false,
    ezaCompUser = false,
    hasEssentialSubscription = false,
    hasGeoPricePromo = false,
    isMobile = false,
    isPaPreview = false,
    botOrFirstTimeVisitor = false,
    subscriptionPreview,
    locale,
    allowsNudity,
    excludeNudity,
    hasRfSubscription,
    isAuthenticated,
    isAnonymous,
  } = gallery?.searchProfile ?? {};

  return useMemo(
    () =>
      factory.fromDTO({
        // Optional on the wire because the fields were added to Rails'
        // searchProfile payload after this adapter shipped; an older cached
        // response can still arrive without them. The fallbacks match Ruby's
        // own defaults for a market with no special handling.
        allowsNudity: allowsNudity ?? true,
        excludeNudity: excludeNudity ?? true,
        authState: toAuthState(isAuthenticated, isAnonymous),
        isSubscriptionRepackagingActive,
        upsellTestEligible,
        hasNLSKeywordInterleaveForMostPopular,
        hasCustomMediaAgreements,
        ezaCompUser,
        hasEssentialSubscription,
        hasGeoPricePromo,
        isMobile,
        isPaPreview,
        botOrFirstTimeVisitor,
        subscriptionPreview,
        locale,
        hasRfSubscription,
      }),
    [
      allowsNudity,
      excludeNudity,
      isAuthenticated,
      isAnonymous,
      isSubscriptionRepackagingActive,
      upsellTestEligible,
      hasNLSKeywordInterleaveForMostPopular,
      hasCustomMediaAgreements,
      ezaCompUser,
      hasEssentialSubscription,
      hasGeoPricePromo,
      isMobile,
      isPaPreview,
      botOrFirstTimeVisitor,
      subscriptionPreview,
      locale,
      hasRfSubscription,
    ]
  );
};

export default useSearchProfileAdapter;
